import { AdminProtocolAccessReportComponent } from './activity-reports/protocol-accesses/admin-protocol-access-reports.component';
import { AdminActivityReportDocumentsComponent } from './activity-reports/documents/admin-activity-report-documents.component';
import { AdminAccessInvestigationReportsComponent } from './activity-reports/access-investigation/admin-access-investigation-reports.component';
import { AdminActivityReportsComponent } from './activity-reports/admin-activity-reports.component';
import { Routes } from '@angular/router';
import { AnnouncementsAdminComponent } from './announcements/announcements-admin.component';
import { CommonLinksGridComponent } from './common-links/common-links-grid.component';
import { ProtocolActivationComponent } from './protocol-activation/protocol-activation.component';
import { AdminDocumentsComponent } from './upload-documents/admin-documents.component';
import { AdminGuard } from './admin-guard';
import { UserProfilesComponent } from './user-profiles/user-profiles.component';
import { WorkspaceLevelLayoutComponent } from '../layout';
import { AdminNavigationComponent } from './navigation/admin-navigation.component';
import { AuditTrailExportComponent } from './audit-trail-export/audit-trail-export.component';
import { AdminLevelComponent } from './admin-level.component';
import { SetDefaultAdminDocumentsFiltersGuard } from './upload-documents/set-default-admin-documents-filters-guard';
import { UserPermissionGuard } from '../user-permissions/user-permission-guard';
import { permission } from '../user-permissions/route-permission';
import { PageTitle } from '../core/page-title';
import { WorkspaceDrawerBehaviorResolver } from '../layout/workspace-drawer-behavior-resolver';
import { routeData, routeParams } from '../shared/route-helpers';
import { Settings } from '../core/settings';
import { SetDefaultAdminReportsFiltersGuard } from './activity-reports/documents/set-default-admin-reports-filters-guard';
import { AdminKitInventoryComponent } from './kit-inventory/admin-kit-inventory.component';
import { AdminKitInventoryOnHandComponent } from './kit-inventory/admin-kit-inventory-onhand.component';
import { AdminKitInventoryExpirationComponent } from './kit-inventory/admin-kit-inventory-expiration.component';
import { SponsorKitInventoryFiltersGuard } from '../sponsor/kit-inventory/sponsor-kit-inventory-filters-guard';
import { AdminEQuerySummaryComponent } from './e-query/e-query-summary/admin-e-query-summary.component';
import { AdminEQueryHideComponent } from './e-query/e-query-hide-unhide/admin-e-query-hide.component';
import { AdminEscalationEmailsComponent } from './e-query/admin-escalations/admin-escalation-emails.component';
import { AdminEQueryTabsComponent } from './e-query/e-query-tabs/admin-e-query-tabs.component';
import { CoordinatorEQueryTabsComponent } from './e-query/e-query-tabs/coordinator-e-query-tabs.component';
import { UserAudienceRoleGuard, audienceRole } from '../user';
import { PrefilledEQueryCreateFormComponent } from './e-query/prefiled-query-edit/prefilled-e-query-create-form.component';
import {AdminUsageEQueryReportComponent} from './e-query/usage-reports/admin-usage-e-query-report.component';
import { AdminEmailNotificationAlertsComponent } from './activity-reports/email-alerts/admin-email-notification-alerts-reports.component';
import { SetDefaultAdminEmailNotificationFiltersGuard } from './activity-reports/email-alerts/set-default-admin-email-notification-filters-guard';
import { AdminEquerySummaryFiltersGuard } from './e-query/e-query-summary/admin-e-query-summary-filters-guard';
import { EditDefaultColumnsComponent } from './edit-default-columns/edit-columns.component';

export const adminRoutes: Routes = [
    {
        path: 'admin',
        canActivate: [AdminGuard],
        component: AdminLevelComponent,
        resolve: {
            [routeData.drawerBehavior]: WorkspaceDrawerBehaviorResolver,
        },
        children: [
            {
                path: '',
                component: WorkspaceLevelLayoutComponent,
                children: [
                    {
                        path: 'announcements',
                        pathMatch: 'full',
                        data: {title: PageTitle.getAdminTitle('Announcements')},
                        component: AnnouncementsAdminComponent
                    },
                    {
                        path: 'common-links',
                        canActivate: [UserPermissionGuard],
                        data: {
                            ...permission('UploadCommonLinks'),
                            title: PageTitle.getAdminTitle('Common Links'),
                        },
                        component: CommonLinksGridComponent
                    },
                    {
                        path: 'protocols',
                        canActivate: [UserPermissionGuard],
                        data: {
                            ...permission(['ProtocolAccess', 'ProtocolReadAccess']),
                            title: PageTitle.getAdminTitle('Protocol Access'),
                        },
                        component: ProtocolActivationComponent
                    },
                    {
                        path: 'user-profiles',
                        canActivate: [UserPermissionGuard],
                        data: {
                            ...permission('AccessUserProfile'),
                            title: PageTitle.getAdminTitle('User Profiles'),
                        },
                        component: UserProfilesComponent
                    },
                    {
                        path: 'audit-logs',
                        canActivate: [UserPermissionGuard],
                        data: {
                            ...permission('ViewAuditLogs'),
                            title: PageTitle.getAdminTitle('Audit Logs'),
                        },
                        component: AuditTrailExportComponent
                    },
                    {
                        path: 'documents',
                        children: [
                            {
                                path: '',
                                redirectTo: 'general-manuals',
                                pathMatch: 'full'
                            },
                            {
                                path: `:${routeParams.categoryId}`,
                                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                                canActivate: [SetDefaultAdminDocumentsFiltersGuard],
                                data: {
                                    [routeData.filterDefaultDateRange]: (s: Settings) => s.documentsFilterDefaultDateRange,
                                    title: PageTitle.getAdminTitle('Documents'),
                                },
                                component: AdminDocumentsComponent
                            }
                        ]
                    },
                    {
                        path: 'activity-reports',
                        data: {title: PageTitle.getAdminTitle('Activity Reports')},
                        component: AdminActivityReportsComponent,
                        children: [
                            {
                                path: '',
                                redirectTo: 'documents',
                                pathMatch: 'full'
                            },
                            {
                                path: 'documents',
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'equery',
                                        pathMatch: 'full'
                                    },
                                    {
                                        path: `:${routeParams.categoryId}`,
                                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                                        canActivate: [SetDefaultAdminReportsFiltersGuard],
                                        data: {
                                            [routeData.filterDefaultDateRange]: (s: Settings) => s.activityReportsFilterDefaultDateRange,
                                        },
                                        component: AdminActivityReportDocumentsComponent
                                    }
                                ]
                            },
                            {
                                path: 'protocol-access',
                                component: AdminProtocolAccessReportComponent
                            },
                            {
                                path: 'access-investigation',
                                canActivate: [UserPermissionGuard],
                                data: permission('AccessInvestigationReport'),
                                component: AdminAccessInvestigationReportsComponent
                            },
                            {
                                path: 'email-alerts',
                                canActivate: [SetDefaultAdminEmailNotificationFiltersGuard],
                                component: AdminEmailNotificationAlertsComponent,
                            }
                        ]
                    },
                    {
                        path: 'kit-inventory',
                        data: { title: PageTitle.getAdminTitle('Kit Inventory') },
                        component: AdminKitInventoryComponent,
                        children: [
                            {
                                path: '',
                                redirectTo: 'inventory-summary',
                                pathMatch: 'full'
                            },
                            {
                                path: 'inventory-summary',
                                component: AdminKitInventoryOnHandComponent
                            },
                            {
                                path: 'on-hand-expiration',
                                canActivate: [SponsorKitInventoryFiltersGuard],
                                component: AdminKitInventoryExpirationComponent
                            }
                        ]
                    },
                    {
                        path: 'edit/columns',
                        data: {
                            title: PageTitle.getAdminTitle('Edit Columns'),
                            ...audienceRole('Admin'),
                            ...permission('ConfigureViews'),
                        },
                        component: EditDefaultColumnsComponent,
                    },
                    {
                        path: `coordinator-e-query/e-query/new-e-query/:${routeParams.eQueryStageId}`,
                        data: {
                            ...audienceRole(['Internal', 'Admin']),
                            ...permission('EQueryCreationRespondClose'),
                        },
                        canActivate: [UserAudienceRoleGuard, UserPermissionGuard],
                        component: PrefilledEQueryCreateFormComponent
                    },
                    {
                        path: 'coordinator-e-query',
                        data: {
                            title: PageTitle.getAdminTitle('eQueries'),
                            ...audienceRole('Internal')
                        },
                        component: CoordinatorEQueryTabsComponent,
                        canActivate: [UserAudienceRoleGuard],
                        children: [
                            {
                                path: '',
                                redirectTo: 'query-summary',
                                pathMatch: 'full'
                            },
                            {
                                path: 'query-summary',
                                component: AdminEQuerySummaryComponent,
                                data: permission(['EQueryCreationRespondClose', 'EQueryViewAndRespond']),
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'hide',
                                data: permission('EQueryAdministration'),
                                component: AdminEQueryHideComponent,
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'escalation-emails',
                                data: permission('EQueryAdministration'),
                                component: AdminEscalationEmailsComponent,
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'usage-report',
                                data: permission('EQueryAdministration'),
                                component: AdminUsageEQueryReportComponent,
                                canActivate: [UserPermissionGuard],
                            },
                        ]
                    },
                    {
                        path: 'admin-e-query',
                        data: {
                            title: PageTitle.getAdminTitle('eQueries'),
                            ...audienceRole('Admin'),
                        },
                        component: AdminEQueryTabsComponent,
                        canActivate: [UserAudienceRoleGuard],
                        children: [
                            {
                                path: 'query-summary',
                                component: AdminEQuerySummaryComponent,
                                data: permission(['EQueryCreationRespondClose', 'EQueryViewAndRespond']),
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'hide',
                                component: AdminEQueryHideComponent,
                                data: permission('EQueryAdministration'),
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'escalation-emails',
                                component: AdminEscalationEmailsComponent,
                                data: permission('EQueryAdministration'),
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: 'usage-report',
                                component: AdminUsageEQueryReportComponent,
                                data: permission('EQueryAdministration'),
                                canActivate: [UserPermissionGuard],
                            },
                            {
                                path: '',
                                redirectTo: 'query-summary',
                                pathMatch: 'full'
                            },
                        ]
                    },
                    {
                        path: '',
                        component: AdminNavigationComponent,
                        outlet: 'navigation'
                    }
                ]
            }
        ]
    }
];
